import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, Empty, Field } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import EarningsCard from "../components/EarningsCard";

const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); d.setDate(1); return iso(d); };

export default function Earnings() {
  const { user } = useApp();
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState({ date_from: monthStart(), date_to: iso(new Date()) });
  const [rows, setRows] = useState(null);
  const all = ["owner", "accountant"].includes(user.role);
  const load = useCallback(() => api.get("/earnings", { params: range }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [range]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!params.get("stripe")) return;
    toast.success(params.get("stripe") === "done" ? "Stripe setup saved – checking status" : "Please finish your Stripe setup");
    setParams({}, { replace: true });
  }, [params, setParams]);
  const exportCsv = async () => {
    try {
      const { data } = await api.get(`/earnings/${all ? "all" : user.id}/export.csv`, { params: range, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = `earnings_${range.date_from}_${range.date_to}.csv`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Practitioners" title="Earnings & payouts" />
      <div className="flex flex-wrap items-end gap-3 mb-6" data-testid="earnings-filters">
        <Field label="From"><Input type="date" data-testid="earnings-from" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} /></Field>
        <Field label="To"><Input type="date" data-testid="earnings-to" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} /></Field>
        <Button variant="outline" className="rounded-full" data-testid="earnings-export" onClick={exportCsv}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4 max-w-3xl">Earnings = commission % of the treatment price + travel % of the travel fee, for every completed appointment (clinic and home). "Owed" is everything earned so far minus payouts recorded.</p>
      {rows && !rows.length && <Empty text="No practitioners yet" testId="earnings-empty" />}
      <div className="space-y-4">{(rows || []).map((r) => <EarningsCard key={r.user_id} r={r} range={range} onChange={load} />)}</div>
    </div>
  );
}
